import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Users } from "lucide-react";

const NAMES = [
  "Ahmad Rizal", "Muhammad Hafiz", "Faizal Azman", "Danish Irfan", "Aiman Syafiq",
  "Haziq Farhan", "Irfan Hakim", "Syafiq Nabil", "Arif Zulkifli", "Ridwan Ashraf",
  "Khairul Anwar", "Shahrul Nizam", "Fitri Razak", "Luqman Hakim", "Amirul Aqil",
  "Nabil Faris", "Zaid Ikmal", "Danial Amsyar", "Farhan Azmi", "Naim Iskandar",
  "Nurul Aisyah", "Siti Fatimah", "Sarah Nabila", "Ain Liyana", "Farah Amira",
  "Athirah Balqis", "Najwa Hana", "Syafiqah Adriana", "Hanis Maisarah", "Aqilah Izzah",
  "Afiq Danish", "Izzat Hakim", "Azlan Mukhriz", "Hafizuddin", "Syahmi Aiman",
  "Akmal Idris", "Fikri Zafran", "Amsyar Hadif", "Qayyum Harith", "Hariz Danial",
  "Aina Sofea", "Puteri Alya", "Damia Irdina", "Batrisyia Nur", "Adlina Huda",
  "Zulaikha Amani", "Mariam Yasmin", "Husna Aqilah", "Alisa Zahra", "Iman Safiyyah",
  "Razin Arif", "Hakeem Firdaus", "Imran Zikri", "Bazli Ashman", "Faez Afdlin",
  "Anis Hazirah", "Dania Insyirah", "Safiya Nour", "Qistina Humaira", "Alysha Damia",
];

const LOCATIONS = [
  "Kuala Lumpur", "Johor Bahru", "Penang", "Kota Kinabalu", "Kuching",
  "Shah Alam", "Ipoh", "Melaka", "Seremban", "Kuantan",
  "Kota Bharu", "Terengganu", "Alor Setar", "Miri", "Sandakan",
  "Petaling Jaya", "Subang Jaya", "Cyberjaya", "Putrajaya", "Klang",
  "Rawang", "Taiping", "Butterworth", "Sungai Petani", "Batu Pahat",
];

const AVATAR_COLORS = [
  "#39FF14", "#4FC3F7", "#FF6B6B", "#FFD93D", "#6BCB77",
  "#845EC2", "#FF9671", "#00C9A7", "#FFC75F", "#D65DB1",
];

function shuffleArray(arr) {
  const shuffled = [...arr];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

function generateQueue() {
  const shuffledNames = shuffleArray(NAMES);
  return shuffledNames.map((name) => ({
    name,
    location: LOCATIONS[Math.floor(Math.random() * LOCATIONS.length)],
    color: AVATAR_COLORS[Math.floor(Math.random() * AVATAR_COLORS.length)],
    timeAgo: Math.random() < 0.5 ? "baru sahaja" : `${Math.floor(Math.random() * 4) + 1} minit lepas`,
  }));
}

const JoinNotification = () => {
  const [queue, setQueue] = useState(() => generateQueue());
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(false);

  const advance = useCallback(() => {
    setVisible(false);
    const timeout = setTimeout(() => {
      setIndex((prev) => {
        const next = prev + 1;
        if (next >= queue.length) {
          setQueue(generateQueue());
          return 0;
        }
        return next;
      });
      setVisible(true);
    }, 400);
    return timeout;
  }, [queue.length]);

  useEffect(() => {
    const initialDelay = setTimeout(() => setVisible(true), 1500);
    return () => clearTimeout(initialDelay);
  }, []);

  useEffect(() => {
    if (!visible) return;
    const timer = setTimeout(() => advance(), 3000);
    return () => clearTimeout(timer);
  }, [visible, advance]);

  const person = queue[index];
  if (!person) return null;

  const initials = person.name.split(" ").map((w) => w[0]).join("").slice(0, 2);

  return (
    <div className="fixed bottom-3 left-3 sm:bottom-4 sm:left-4 z-50 pointer-events-none" data-testid="join-notification-wrapper">
      <AnimatePresence mode="wait">
        {visible && (
          <motion.div
            key={`${person.name}-${index}`}
            initial={{ opacity: 0, y: 30, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="flex items-center gap-3 px-4 py-3 rounded-xl pointer-events-auto"
            style={{
              background: "rgba(10,10,10,0.75)",
              backdropFilter: "blur(16px)",
              WebkitBackdropFilter: "blur(16px)",
              border: "1px solid rgba(57,255,20,0.15)",
              boxShadow: "0 4px 24px rgba(0,0,0,0.5), 0 0 12px rgba(57,255,20,0.08)",
            }}
            data-testid="join-notification-card"
          >
            {/* Avatar */}
            <div
              className="relative w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-xs font-bold"
              style={{
                background: `${person.color}20`,
                color: person.color,
                border: `1.5px solid ${person.color}40`,
              }}
              data-testid="join-notification-avatar"
            >
              {initials}
              <span
                className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full"
                style={{
                  background: "#39FF14",
                  boxShadow: "0 0 6px #39FF14",
                  animation: "pulse 2s ease-in-out infinite",
                }}
              />
            </div>

            {/* Info */}
            <div className="flex flex-col min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-white text-sm font-semibold truncate max-w-[140px]" data-testid="join-notification-name">
                  {person.name}
                </span>
              </div>
              <span className="text-gray-400 text-[11px] leading-tight flex items-center gap-1">
                <Users className="w-3 h-3 shrink-0 text-[#39FF14]" />
                joined community
                <span className="text-gray-500 ml-0.5">· {person.timeAgo}</span>
              </span>
              <span className="text-gray-500 text-[10px] leading-tight mt-0.5">
                {person.location}
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default JoinNotification;
